import { Push } from '../api/apiClient';

/**
 * Push-Benachrichtigungen – lokale Geräteeinstellung.
 *
 * Welche Benachrichtigungen ein Gerät erhält, steht in localStorage dieses
 * Geräts (nicht im Benutzerkonto). Damit die API weiß, was sie an das Gerät
 * senden soll, wird die Auswahl zusammen mit dem Web-Push-Abonnement des
 * Browsers an /api/Push/abonnieren übertragen. Die Anzeige übernimmt der
 * Service Worker (public/service-worker.js).
 */

const STORAGE_KEY = 'benachrichtigungsEinstellungen';

export const VORLAUF_OPTIONEN = [
  { minuten: 30, label: '30 Minuten' },
  { minuten: 60, label: '1 Stunde' },
  { minuten: 120, label: '2 Stunden' },
  { minuten: 180, label: '3 Stunden' },
  { minuten: 360, label: '6 Stunden' },
  { minuten: 720, label: '12 Stunden' },
  { minuten: 1440, label: '1 Tag' },
  { minuten: 2880, label: '2 Tage' },
];

export const STANDARD_EINSTELLUNGEN = {
  vorWettkampf: false,
  vorlaufMinuten: 120,
  unterschrift: false,
  eigeneMannschaft: false,
  eigeneKlasse: false,
  terminAenderung: false,
  mitteilungen: false,
};

export function leseEinstellungen() {
  try {
    const gespeichert = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    return { ...STANDARD_EINSTELLUNGEN, ...(gespeichert && typeof gespeichert === 'object' ? gespeichert : {}) };
  } catch {
    return { ...STANDARD_EINSTELLUNGEN };
  }
}

function schreibeEinstellungen(einstellungen) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(einstellungen));
  } catch {
    // Ohne localStorage gilt die Auswahl nur bis zum Neuladen.
  }
}

export function istEineAktiv(e) {
  return Boolean(e.vorWettkampf || e.unterschrift || e.eigeneMannschaft || e.eigeneKlasse
    || e.terminAenderung || e.mitteilungen);
}

/** iPhone/iPad – iPadOS meldet sich als „MacIntel", erkennbar am Touch-Display. */
function istIos() {
  const { userAgent, platform, maxTouchPoints } = window.navigator;
  return /iPad|iPhone|iPod/.test(userAgent) || (platform === 'MacIntel' && maxTouchPoints > 1);
}

function laeuftAlsApp() {
  return Boolean(window.matchMedia?.('(display-mode: standalone)').matches)
    || window.navigator.standalone === true;
}

/**
 * Was dieses Gerät kann:
 *  - 'ok': Push wird unterstützt
 *  - 'ios-installieren': iPhone/iPad – Push gibt es nur in der zum Home-Bildschirm hinzugefügten App
 *  - 'nicht-unterstuetzt': Browser ohne Service Worker / Push API
 */
export function pruefeUnterstuetzung() {
  if (istIos() && !laeuftAlsApp()) return 'ios-installieren';
  const hatPush = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  return hatPush ? 'ok' : 'nicht-unterstuetzt';
}

/** 'granted' | 'denied' | 'default' | null (keine Notification API) */
export function berechtigung() {
  return 'Notification' in window ? Notification.permission : null;
}

function schluesselAlsBytes(base64Url) {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const roh = atob((base64Url + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(roh, (c) => c.charCodeAt(0));
}

function gleicheBytes(a, b) {
  if (!a || !b) return false;
  const x = new Uint8Array(a);
  const y = new Uint8Array(b);
  return x.length === y.length && x.every((v, i) => v === y[i]);
}

async function registrierung() {
  // Der Service Worker wird in public/index.html registriert. Wartet höchstens
  // kurz – ohne Registrierung (z. B. blockiert) kann es kein Abonnement geben.
  const bereit = navigator.serviceWorker.ready;
  const zeitlimit = new Promise((_, reject) => {
    setTimeout(() => reject(new Error('Der Service Worker ist nicht aktiv. Bitte die Seite neu laden.')), 10000);
  });
  return Promise.race([bereit, zeitlimit]);
}

/** Holt das Abonnement des Browsers bzw. legt es an (mit dem aktuellen Schlüssel der API). */
async function sicherstellenAbonnement() {
  const reg = await registrierung();
  const { publicKey } = await Push.getSchluessel();
  const schluessel = schluesselAlsBytes(publicKey);

  let abo = await reg.pushManager.getSubscription();
  // Hat die API einen neuen Schlüssel, ist das alte Abonnement wertlos.
  if (abo && !gleicheBytes(abo.options?.applicationServerKey, schluessel)) {
    await abo.unsubscribe().catch(() => {});
    abo = null;
  }
  if (!abo) {
    abo = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: schluessel });
  }
  return abo;
}

async function vorhandenesAbonnement() {
  if (pruefeUnterstuetzung() !== 'ok') return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    return (await reg?.pushManager.getSubscription()) ?? null;
  } catch {
    return null;
  }
}

async function anApiMelden(abo, e) {
  const json = abo.toJSON();
  await Push.abonnieren({
    endpoint: json.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth,
    vorWettkampf: Boolean(e.vorWettkampf),
    vorlaufMinuten: Number(e.vorlaufMinuten) || STANDARD_EINSTELLUNGEN.vorlaufMinuten,
    unterschrift: Boolean(e.unterschrift),
    eigeneMannschaft: Boolean(e.eigeneMannschaft),
    eigeneKlasse: Boolean(e.eigeneKlasse),
    terminAenderung: Boolean(e.terminAenderung),
    mitteilungen: Boolean(e.mitteilungen),
  });
}

/**
 * Speichert die Auswahl auf dem Gerät und meldet das Gerät passend an bzw. ab.
 * Muss für die Berechtigungsabfrage aus einer Benutzeraktion heraus aufgerufen werden.
 * Wirft einen Fehler mit verständlicher Meldung, wenn es nicht klappt – die
 * Auswahl bleibt dann unverändert.
 */
export async function speichereEinstellungen(neu) {
  if (!istEineAktiv(neu)) {
    const abo = await vorhandenesAbonnement();
    if (abo) {
      await Push.abmelden(abo.endpoint, { loeschen: true }).catch(() => {});
      await abo.unsubscribe().catch(() => {});
    }
    schreibeEinstellungen(neu);
    return;
  }

  if (pruefeUnterstuetzung() !== 'ok') {
    throw new Error('Dieses Gerät bzw. dieser Browser unterstützt keine Benachrichtigungen.');
  }

  const erlaubnis = Notification.permission === 'granted'
    ? 'granted'
    : await Notification.requestPermission();
  if (erlaubnis !== 'granted') {
    throw new Error('Benachrichtigungen wurden im Browser nicht erlaubt. Bitte in den Browser- bzw. Systemeinstellungen für diese Seite freigeben.');
  }

  const abo = await sicherstellenAbonnement();
  await anApiMelden(abo, neu);
  schreibeEinstellungen(neu);
}

/**
 * Beim App-Start (angemeldet): Abonnement mit der gespeicherten Auswahl
 * erneut an die API melden. Hält die Zuordnung zum angemeldeten Benutzer und
 * einen evtl. geänderten Schlüssel aktuell. Fragt nie nach einer Berechtigung.
 */
export async function synchronisiereEinstellungen() {
  const e = leseEinstellungen();
  if (!istEineAktiv(e)) return;
  if (pruefeUnterstuetzung() !== 'ok' || Notification.permission !== 'granted') return;
  try {
    const abo = await sicherstellenAbonnement();
    await anApiMelden(abo, e);
  } catch {
    // Nächster App-Start versucht es erneut.
  }
}

/**
 * Bei der Abmeldung: Gerät auf dem Server vom Benutzer lösen, solange das
 * Token noch gültig ist. Die Auswahl auf dem Gerät bleibt erhalten und gilt
 * nach der nächsten Anmeldung wieder.
 */
export async function geraetAbmelden() {
  // Token sofort sichern – die Abmeldung entfernt es gleich nach diesem Aufruf.
  const token = localStorage.getItem('authToken');
  if (!token) return;
  const abo = await vorhandenesAbonnement();
  if (!abo) return;
  await Push.abmelden(abo.endpoint, { token }).catch(() => {});
}

/** Testbenachrichtigung an dieses Gerät. */
export async function sendeTest() {
  const abo = await vorhandenesAbonnement();
  if (!abo) throw new Error('Dieses Gerät ist nicht für Benachrichtigungen angemeldet.');
  await Push.test(abo.endpoint);
}
